import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { buildAreaTitle, buildPermissionsHeader, ScopesByGroup, PermissionsEmptyState } from './RoleHelpers';
import Search from 'wix-ui-icons-common/Search';
import ChevronRight from 'wix-ui-icons-common/ChevronRight';
import { Area } from '../../types/areas';
import { useCallback, useMemo, useState } from 'react';
import * as s from './RolePermissions.scss';

import { Card, Accordion, Page, Input } from 'wix-style-react';

interface IAppProps extends InjectedTranslateProps {
  areas: Area[];
  selectedScopes: string[];
  disabledScopes: string[];
  readOnly: boolean;

  onScopeUpdate(scope: string): void;
  onAreaSelectAllUpdate(scopeIds: string[]): void;
}

export const RolePermissions: React.FC<IAppProps> = ({ t, areas, selectedScopes, disabledScopes, readOnly, onScopeUpdate, onAreaSelectAllUpdate }) => {

  const [filterValue, setFilterValue] = useState<string>('');

  const filteredAreas = useMemo<Partial<Area[]>>(() => {
    return filterAreas(areas, filterValue);
  }, [areas, filterValue]);

  const onFilterChange = useCallback((event) => {
    setFilterValue(event?.target?.value || '');
  }, []);

  const clearFilter = useCallback(() => {
    setFilterValue('');
  }, []);

  return (
    <Card>
       <Page.Sticky style={{ backgroundColor: 'inherit', borderRadius: 'inherit' }}>
        <Card.Header
          title={buildPermissionsHeader(t, !filterValue, selectedScopes)}
          subtitle={t(`role.choosePermissionsToGrant${readOnly ? '' : '.edit'}`)}
          suffix={(<Input
            className={s.permissionsSearchInput}
            placeholder={t('role.filter')}
            roundInput
            value={filterValue}
            clearButton
            onClear={onFilterChange}
            onChange={onFilterChange}
            dataHook={'rolePermissions-filter-input'}
            prefix={<Input.IconAffix>
              <Search/>
            </Input.IconAffix>}
          />)}
        />
       </Page.Sticky>
       <Card.Divider />
      {
        filteredAreas.length > 0 ?
          (<Accordion
            key={filteredAreas.map(filteredArea => filteredArea.title).join(',')}
            dataHook={'rolePermissions-area-accordion'}
            multiple
            skin="light"
            items={filteredAreas
              .map(({ title, scopes }) => ({
                title: buildAreaTitle(title, !filterValue, scopes, selectedScopes),
                icon: <ChevronRight style={{ fill: '3899ec' }}/>,
                children: (<ScopesByGroup
                  scopes={scopes}
                  onScopeUpdate={onScopeUpdate}
                  readOnly={readOnly}
                  selectedScopes={selectedScopes}
                  disabledScopes={disabledScopes}
                  onAreaSelectAllUpdate={onAreaSelectAllUpdate}/>),
              }))}
          />) :
          (<Card.Content>
            { PermissionsEmptyState(t, clearFilter) }
          </Card.Content>)
      }
    </Card>
  );
};

const shouldShowTitle = (filterValue: string, { title }: { title: string}): boolean => {
  const trimmedTitle = title.trim().toLowerCase();
  const trimmedFilterValue = filterValue.trim().toLowerCase();
  return trimmedTitle.includes(trimmedFilterValue);
};

const filterAreas = (areas: Area[], filterValue: string): Partial<Area[]> => {
  return areas.reduce((acc, area) => {
    if (shouldShowTitle(filterValue, area)) {
      acc.push(area);
    } else {
      const filterScopes = area.scopes.filter(shouldShowTitle.bind(null, filterValue));
      if (filterScopes.length > 0) {
        acc.push({ title: area.title, scopes: filterScopes });
      }
    }
    return acc;
  }, []);
};

export default translate()(RolePermissions);
